import { Component, DoCheck, effect, inject, input, viewChildren } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ExportCsvService } from 'src/app/services/export-csv.service';
import { CsvModes } from 'src/app/core/csv-modes.class';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DealHierarchy } from 'src/app/models/classes/deal-hierarchy.class';
import { InvestmentTypeHierarchy } from 'src/app/models/classes/investment-type-hierarchy.class';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { ExpandedViewSliderComponent } from 'src/app/shared/expanded-view-slider/expanded-view-slider.component';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { IrrValueFormatPipe } from 'src/app/pipes/irr-value-format.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { MatSortModule } from '@angular/material/sort';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';
import { PortfolioSeries } from 'src/app/models/classes/portfolio-series.class';
import { SeriesTableConfigService } from 'src/app/services/series-table-config.service';
import { TableLogicService } from 'src/app/services/table-logic.service';

@Component({
    selector: 'app-series-deals-table-real-estate',
    imports: [
        CommonModule,
        ExpandedViewSliderComponent,
        MatTableModule,
        CurrencyFormatPipe,
        TvpiValueFormatPipe,
        IrrValueFormatPipe,
        RouterModule,
        DateFormatPipe,
        MatIconModule,
        TourMatMenu,
        MatSortModule
    ],
    providers: [TvpiValueFormatPipe, IrrValueFormatPipe],
    templateUrl: './series-deals-table-real-estate.component.html',
    styleUrls: ['../../series-deals-table/series-deals-table.component.scss']
})
export class SeriesDealsTableRealEstateComponent implements DoCheck {
  private exportCsvService = inject(ExportCsvService);
  private dateFormatPipe = inject(DateFormatPipe);
  private tvpiValueFormatPipe = inject(TvpiValueFormatPipe);
  private irrValueFormatPipe = inject(IrrValueFormatPipe);
  activityTracking = inject(ActivityTrackingService);
  private seriesTableConfig = inject(SeriesTableConfigService);
  private tableLogicService = inject(TableLogicService);

  readonly iconSet = input<Record<string, string> | undefined>(undefined);
  readonly investmentTypes = input<InvestmentTypeHierarchy[]>([]);
  readonly summary = input<AggregatedPortfolioSeries | PortfolioSeries | undefined>(undefined);
  readonly portfolioName = input<string>('');
  readonly seriesName = input<string>('');
  readonly currency = input<string | undefined>(undefined);
  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);
  readonly reportingPeriod = input<string | undefined>(undefined);
  readonly isAggregatedSeries = input<boolean>(false);
  private static readonly columns = ['name', 'commitmentDate', 'sector', 'commitment', 'contributions', 'distributions', 'nav', 'totalValue', 'tvpi', 'irr'];
  private static readonly expandedColumns = ['name', 'realizationType', 'commitmentDate', 'sector', 'valuationDate', 'commitment', 'contributions', 'remainingCommitment', 'distributions', 'reportedValue', 'subsequentContributions', 'subsequentDistributions', 'fxGainLoss', 'nav', 'totalValue', 'tvpi', 'irr'];
  expandView = false;
  columnsToDisplay: string[] = SeriesDealsTableRealEstateComponent.columns;
  readonly sorts = viewChildren(MatSort);

  deals: DealHierarchy[] = [];
  dataSource = new MatTableDataSource<DealHierarchy>([]);
  sortingInitialized = false;
  displayShadowOnTable = false;

  constructor() {
    effect(() => {
      if (this.investmentTypes() && this.investmentTypes().length > 0) {
        this.formatTableValues();
        this.sortingInitialized = false;
      }
    });
  }

  formatTableValues() {
    this.deals = this.tableLogicService.sortDealsByCommitmentDate(
      this.investmentTypes().flatMap((investmentType) => investmentType.deals),
    );
    this.deals.forEach(deal => {
      deal.showHoldings = this.tableLogicService.checkDealToHoldingMapping(deal.holdings);
      deal.disableDrillThrough = this.tableLogicService.isDealDrillThroughDisabled(deal);
    });
    this.dataSource = new MatTableDataSource(this.deals);
  }

  ngDoCheck() {
    this.initializeSorting();
  }

  initializeSorting() {
    if (!this.sortingInitialized && this.sorts().length > 0) {
      this.dataSource.sort = this.sorts()[0];
      this.sortingInitialized = true;
    }
  }

  exportSeriesDealsDataToCsv(event: MouseEvent) {
    const seriesName = this.seriesName();
    this.activityTracking.exportToExcelClick('series deals table', seriesName);
    (event.target as HTMLButtonElement).disabled = true;
    const cfg = this.seriesTableConfig.config();
    const seriesDealsArray = [], seriesHoldingsArray = [];
    const filename = this.exportCsvService.getExportFilename(CsvModes.seriesDeals, this.reportingPeriod() ?? '', this.portfolioName(), seriesName);
    const headerForSeriesDeals = this.buildRealEstateCsvHeader(this.exportCsvService.getCsvHeader(CsvModes.seriesDeals, cfg.classification.headerLabel) as unknown as Record<string, string>);
    const headerForSeriesHoldings = this.buildRealEstateCsvHeader(this.exportCsvService.getCsvHeader(CsvModes.seriesHoldings, cfg.classification.headerLabel) as unknown as Record<string, string>);
    seriesDealsArray.push(headerForSeriesDeals);
    seriesHoldingsArray.push(headerForSeriesHoldings);

    const sortedDeals = this.exportCsvService.sortArrayAscending(this.deals);
    sortedDeals.forEach((deal: DealHierarchy) => {
      const dealData: Record<string, unknown> = {};
      dealData['dealName'] = deal.name;
      dealData['invStatus'] = deal.realizationType;
      dealData['commitmentDate'] = this.dateFormatPipe.transform(deal.dealCommitmentDate, 'MMM-yy');
      dealData['strategy'] = deal[cfg.classification.field];
      dealData['valuationDateFormatted'] = this.dateFormatPipe.transform(deal.valuationDate, 'M/d/yy');
      dealData['dealCommitment'] = deal.commitment;
      dealData['dealReportedContributions'] = deal.reportedContributions;
      dealData['dealContributions'] = deal.contributions;
      dealData['dealRemainingCommitment'] = deal.remainingCommitment;
      dealData['dealReportedDistributions'] = deal.reportedDistributions;
      dealData['dealDistributions'] = deal.distributions;
      dealData['dealReportedValue'] = deal.reportedValue;
      dealData['dealSubsequentContributions'] = deal.subsequentContributions;
      dealData['dealSubsequentDistributions'] = deal.subsequentDistributions;
      dealData['dealFxGainLoss'] = deal.fxGainLoss;
      dealData['dealNav'] = deal.nav;
      dealData['dealTotalValue'] = deal.totalValue;
      dealData['dealTVPI'] = this.tvpiValueFormatPipe.transform(deal.tvpi);
      dealData['dealIRR'] = this.irrValueFormatPipe.transform(deal.irr);
      seriesDealsArray.push(dealData);

      const sortedHoldings = this.exportCsvService.sortArrayAscending(deal.holdings);
      sortedHoldings.forEach((holding: DealHierarchy['holdings'][number]) => {
        const data: Record<string, unknown> = {};
        data['dealName'] = deal.name;
        data['holdingName'] = holding.name;
        data['invStatus'] = deal.realizationType;
        data['commitmentDate'] = this.dateFormatPipe.transform(deal.dealCommitmentDate, 'MMM-yy');
        data['strategy'] = deal[cfg.classification.field];
        data['valuationDateFormatted'] = this.dateFormatPipe.transform(holding.valuationDate ?? '', 'M/d/yy');
        data['holdingCommitment'] = holding.commitment;
        data['holdingReportedContributions'] = holding.reportedContributions;
        data['holdingContributions'] = holding.contributions;
        data['holdingRemainingCommitment'] = holding.remainingCommitment;
        data['holdingReportedDistributions'] = holding.reportedDistributions;
        data['holdingDistributions'] = holding.distributions;
        data['holdingReportedValue'] = holding.reportedValue;
        data['holdingSubsequentContributions'] = holding.subsequentContributions;
        data['holdingSubsequentDistributions'] = holding.subsequentDistributions;
        data['holdingFxGainLoss'] = holding.fxGainLoss;
        data['holdingNav'] = holding.nav;
        data['holdingTotalValue'] = holding.totalValue;
        seriesHoldingsArray.push(data);
      })
    })
    const worksheets = [{ name: 'Series Deals', csvArray: seriesDealsArray }, { name: 'Series Holdings', csvArray: seriesHoldingsArray }]
    this.exportCsvService.exportToExcelFile(filename, worksheets);
    (event.target as HTMLButtonElement).disabled = false;
  }

  private buildRealEstateCsvHeader(header: Record<string, string>): Record<string, string> {
    const updatedHeader: Record<string, string> = {};
    for (const [key, label] of Object.entries(header)) {
      if (key === 'invType') continue;
      if (key === 'vintageYear') continue;
      if (key === 'strategy') updatedHeader['commitmentDate'] = 'Commitment Date';
      updatedHeader[key] = label;
    }
    return updatedHeader;
  }

  updateColumns(expandView: boolean) {
    this.expandView = expandView;
    this.activityTracking.tableExpandViewToggle('series holding table', this.seriesName(), expandView);
    this.columnsToDisplay = expandView ? SeriesDealsTableRealEstateComponent.expandedColumns : SeriesDealsTableRealEstateComponent.columns;
  }

  scrollTableHandler(event: Event) {
    this.displayShadowOnTable = (event.target as HTMLElement).scrollLeft > 0;
  }
}
